// Holiday Announcement calendar — auto-rotates by date.
// All date math is pure (no deps). Year is derived from the input date.

export type Announcement = {
  id: string;
  emoji: string;
  message: string;
  ctaLabel: string;
  ctaHref: string;
  // Brand-aligned accent. Uses HSL tokens directly (tied to brand palette).
  accentBg: string; // tailwind/inline bg
  accentFg: string; // text color
  priority: number; // higher wins on overlap
  // returns [start, end] inclusive for the given year
  window: (year: number) => [Date, Date];
};

// ---------- date helpers ----------
const d = (y: number, m: number, day: number) => new Date(y, m - 1, day);

// nth weekday of month (weekday: 0=Sun..6=Sat). n=1..5, or -1 for last.
function nthWeekday(year: number, month: number, weekday: number, n: number): Date {
  if (n > 0) {
    const first = new Date(year, month - 1, 1);
    const offset = (weekday - first.getDay() + 7) % 7;
    return new Date(year, month - 1, 1 + offset + (n - 1) * 7);
  }
  // last
  const last = new Date(year, month, 0);
  const offset = (last.getDay() - weekday + 7) % 7;
  return new Date(year, month - 1, last.getDate() - offset);
}

function addDays(date: Date, days: number): Date {
  const n = new Date(date);
  n.setDate(n.getDate() + days);
  return n;
}

// Anonymous Gregorian Easter algorithm
function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const dd = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - dd - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

// Hanukkah first night (approx; uses known mapping for nearby years)
function hanukkahStart(year: number): Date {
  const map: Record<number, [number, number]> = {
    2024: [12, 25], 2025: [12, 14], 2026: [12, 4], 2027: [12, 24],
    2028: [12, 12], 2029: [12, 1], 2030: [12, 20],
  };
  const [m, day] = map[year] ?? [12, 14];
  return d(year, m, day);
}

// Brand palette tokens (kept inline — these are static brand colors, used only in this banner)
const BRAND = {
  rose:   { bg: "hsl(0 38% 74%)",   fg: "hsl(0 0% 17%)" },
  sage:   { bg: "hsl(95 15% 60%)",  fg: "hsl(0 0% 17%)" },
  gold:   { bg: "hsl(40 55% 55%)",  fg: "hsl(0 0% 17%)" },
  cream:  { bg: "hsl(30 35% 88%)",  fg: "hsl(0 0% 17%)" },
  deepRose:{bg: "hsl(345 45% 45%)", fg: "hsl(30 27% 96%)" },
  forest: { bg: "hsl(150 30% 28%)", fg: "hsl(30 27% 96%)" },
  navy:   { bg: "hsl(220 35% 25%)", fg: "hsl(30 27% 96%)" },
  pumpkin:{ bg: "hsl(25 75% 55%)",  fg: "hsl(0 0% 100%)" },
  patriot:{ bg: "hsl(355 65% 45%)", fg: "hsl(0 0% 100%)" },
  evergreen:{bg:"hsl(150 45% 22%)", fg: "hsl(0 0% 100%)" },
} as const;

// ---------- announcements ----------
export const ANNOUNCEMENTS: Announcement[] = [
  {
    id: "valentines",
    emoji: "💕",
    message: "Valentine's Day Florals — order early to guarantee delivery",
    ctaLabel: "Reserve Bouquets",
    ctaHref: "/shop",
    accentBg: BRAND.deepRose.bg, accentFg: BRAND.deepRose.fg,
    priority: 10,
    window: (y) => [d(y, 2, 1), d(y, 2, 14)],
  },
  {
    id: "easter",
    emoji: "🌷",
    message: "Easter & Spring arrangements — fresh tulips, lilies, and pastels",
    ctaLabel: "Shop Spring",
    ctaHref: "/shop",
    accentBg: BRAND.cream.bg, accentFg: BRAND.cream.fg,
    priority: 10,
    window: (y) => { const e = easterSunday(y); return [addDays(e, -14), e]; },
  },
  {
    id: "mothers-day",
    emoji: "💐",
    message: "Mother's Day is coming — book her bouquet before we sell out",
    ctaLabel: "Order for Mom",
    ctaHref: "/shop",
    accentBg: BRAND.rose.bg, accentFg: BRAND.rose.fg,
    priority: 15,
    window: (y) => [d(y, 4, 25), nthWeekday(y, 5, 0, 2)],
  },
  {
    id: "memorial-day",
    emoji: "🌿",
    message: "Memorial Day — honoring loved ones with custom memorial florals",
    ctaLabel: "Request a Memorial",
    ctaHref: "/floral-questionnaire",
    accentBg: BRAND.forest.bg, accentFg: BRAND.forest.fg,
    priority: 12,
    window: (y) => { const last = nthWeekday(y, 5, 1, -1); return [addDays(last, -10), last]; },
  },
  {
    id: "wedding-season",
    emoji: "💍",
    message: "Wedding season is here — limited 2026 dates still available",
    ctaLabel: "Start Your Proposal",
    ctaHref: "/floral-questionnaire",
    accentBg: BRAND.cream.bg, accentFg: BRAND.cream.fg,
    priority: 8,
    window: (y) => [d(y, 6, 1), d(y, 6, 15)],
  },
  {
    id: "fathers-day",
    emoji: "🌻",
    message: "Father's Day — sunflower & garden bouquets for the dads in your life",
    ctaLabel: "Shop for Dad",
    ctaHref: "/shop",
    accentBg: BRAND.gold.bg, accentFg: BRAND.gold.fg,
    priority: 12,
    window: (y) => { const fd = nthWeekday(y, 6, 0, 3); return [d(y, 6, 7), fd]; },
  },
  {
    id: "july-4",
    emoji: "🎆",
    message: "Red, white & blue arrangements for the 4th of July",
    ctaLabel: "Shop Patriotic",
    ctaHref: "/shop",
    accentBg: BRAND.patriot.bg, accentFg: BRAND.patriot.fg,
    priority: 12,
    window: (y) => [d(y, 6, 25), d(y, 7, 4)],
  },
  {
    id: "grandparents-day",
    emoji: "🌼",
    message: "Grandparents Day — show them love with a fresh bouquet",
    ctaLabel: "Send Flowers",
    ctaHref: "/shop",
    accentBg: BRAND.cream.bg, accentFg: BRAND.cream.fg,
    priority: 10,
    window: (y) => { const gp = nthWeekday(y, 9, 0, 1); return [d(y, 8, 30), gp]; },
  },
  {
    id: "sweetest-day",
    emoji: "🌹",
    message: "Sweetest Day — a sweet surprise for someone you love",
    ctaLabel: "Order Now",
    ctaHref: "/shop",
    accentBg: BRAND.deepRose.bg, accentFg: BRAND.deepRose.fg,
    priority: 11,
    window: (y) => { const sd = nthWeekday(y, 10, 6, 3); return [d(y, 10, 10), sd]; },
  },
  {
    id: "bosses-day",
    emoji: "🌺",
    message: "Boss's Day — say thanks with an elegant arrangement",
    ctaLabel: "Order for the Office",
    ctaHref: "/shop",
    accentBg: BRAND.gold.bg, accentFg: BRAND.gold.fg,
    priority: 9,
    window: (y) => [d(y, 10, 10), d(y, 10, 16)],
  },
  {
    id: "halloween",
    emoji: "🎃",
    message: "Spooky-chic floral arrangements & pumpkin centerpieces",
    ctaLabel: "Shop Halloween",
    ctaHref: "/shop",
    accentBg: BRAND.pumpkin.bg, accentFg: BRAND.pumpkin.fg,
    priority: 10,
    window: (y) => [d(y, 10, 20), d(y, 10, 31)],
  },
  {
    id: "thanksgiving",
    emoji: "🍂",
    message: "Thanksgiving centerpieces — order by Nov 22 for delivery",
    ctaLabel: "Reserve Centerpiece",
    ctaHref: "/shop",
    accentBg: BRAND.pumpkin.bg, accentFg: BRAND.pumpkin.fg,
    priority: 11,
    window: (y) => { const th = nthWeekday(y, 11, 4, 4); return [d(y, 11, 15), th]; },
  },
  {
    id: "small-business-saturday",
    emoji: "🛍️",
    message: "Shop Small Saturday — support a local florist & save 10% with code SHOPLOCAL",
    ctaLabel: "Shop Local",
    ctaHref: "/shop",
    accentBg: BRAND.deepRose.bg, accentFg: BRAND.deepRose.fg,
    priority: 20,
    window: (y) => { const sat = addDays(nthWeekday(y, 11, 4, 4), 2); return [sat, sat]; },
  },
  {
    id: "hanukkah",
    emoji: "🕎",
    message: "Hanukkah arrangements — blue & white florals to brighten the festival",
    ctaLabel: "Order for Hanukkah",
    ctaHref: "/shop",
    accentBg: BRAND.navy.bg, accentFg: BRAND.navy.fg,
    priority: 12,
    window: (y) => { const start = hanukkahStart(y); return [addDays(start, -7), addDays(start, 8)]; },
  },
  {
    id: "christmas",
    emoji: "🎄",
    message: "Christmas wreaths & holiday centerpieces — order by Dec 22",
    ctaLabel: "Shop Holiday",
    ctaHref: "/shop",
    accentBg: BRAND.evergreen.bg, accentFg: BRAND.evergreen.fg,
    priority: 10,
    window: (y) => [d(y, 11, 25), d(y, 12, 25)],
  },
  {
    id: "new-years",
    emoji: "🥂",
    message: "New Year's Eve event florals — book your celebration arrangements",
    ctaLabel: "Book NYE Florals",
    ctaHref: "/floral-questionnaire",
    accentBg: BRAND.gold.bg, accentFg: BRAND.gold.fg,
    priority: 12,
    window: (y) => [d(y, 12, 26), d(y, 12, 31)],
  },
  {
    id: "wedding-booking-push",
    emoji: "💐",
    message: "New Year, new wedding plans — secure your 2026 floral date",
    ctaLabel: "Start Your Proposal",
    ctaHref: "/floral-questionnaire",
    accentBg: BRAND.cream.bg, accentFg: BRAND.cream.fg,
    priority: 6,
    window: (y) => [d(y, 1, 2), d(y, 1, 31)],
  },
];

// Always-on fallback (lowest priority)
const FALLBACK: Announcement = {
  id: "always-on",
  emoji: "🌸",
  message: "40+ years of floral artistry — custom arrangements for every occasion",
  ctaLabel: "Request a Custom Proposal",
  ctaHref: "/floral-questionnaire",
  accentBg: BRAND.rose.bg,
  accentFg: BRAND.rose.fg,
  priority: 0,
  window: (y) => [d(y, 1, 1), d(y, 12, 31)],
};

function inWindow(date: Date, [start, end]: [Date, Date]) {
  const t = date.setHours(0, 0, 0, 0);
  return t >= new Date(start).setHours(0, 0, 0, 0) && t <= new Date(end).setHours(23, 59, 59, 999);
}

export function getActiveAnnouncement(now: Date = new Date()): Announcement {
  const year = now.getFullYear();
  const candidates = ANNOUNCEMENTS.filter((a) => inWindow(new Date(now), a.window(year)));
  if (candidates.length === 0) return FALLBACK;
  // Highest priority wins; ties broken by shorter window (more specific)
  return candidates.sort((a, b) => {
    if (b.priority !== a.priority) return b.priority - a.priority;
    const [as, ae] = a.window(year);
    const [bs, be] = b.window(year);
    return (ae.getTime() - as.getTime()) - (be.getTime() - bs.getTime());
  })[0];
}
